import { TimeLeft } from '../types/event.types';

/**
 * Calculates time left from a given difference in milliseconds
 */
export const calculateTimeLeft = (difference: number): TimeLeft => {
  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

/**
 * Gets formatted date string
 */
export const getFormattedDate = (dateTime: string | undefined): string => {
  if (!dateTime) return "N/A";
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return "N/A";
  
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Gets formatted time string with today check
 */
export const getFormattedTime = (dateTime: string | undefined): string => {
  if (!dateTime) return "N/A";
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return "N/A";
  
  const isToday = date.toDateString() === new Date().toDateString();
  if (isToday) {
    return `Today, ${date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  }
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Gets check-in time (90 minutes before event start)
 */
export const getCheckInFormattedTime = (dateTime: string | undefined): string => {
  if (!dateTime) return "N/A";
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return "N/A";
  
  const checkInTimestamp = date.getTime() - 90 * 60000; // 90 minutes before
  const checkInDate = new Date(checkInTimestamp);
  const isToday = checkInDate.toDateString() === new Date().toDateString();
  
  return isToday
    ? `Today, ${checkInDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
    : `${checkInDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })}, ${checkInDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`;
};

/**
 * Validates if a date string is valid
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Checks if event start time is less than one hour away
 */
export const isLessThanOneHourAway = (eventStartTime: string | undefined): boolean => {
  if (!eventStartTime || !isValidDate(eventStartTime)) return false;
  
  const eventTime = new Date(eventStartTime).getTime();
  const currentTime = Date.now();
  const timeDifference = eventTime - currentTime;
  
  return timeDifference <= 3600000 && timeDifference > 0; // 1 hour in milliseconds
};

/**
 * Checks if check-in is enabled (90 minutes before event or less)
 */
export const isCheckInEnabled = (eventStartTime: string | undefined): boolean => {
  if (!eventStartTime || !isValidDate(eventStartTime)) return false;
  
  const eventTime = new Date(eventStartTime).getTime();
  const checkInTime = eventTime - (90 * 60 * 1000); // 90 minutes before
  const currentTime = Date.now();
  
  return currentTime >= checkInTime && currentTime <= eventTime;
};

/**
 * Formats stakes array to readable string
 */
export const formatStakes = (stakes: Array<{ type: string; quantity: number }>): string => {
  if (!stakes || stakes.length === 0) return "No Stakes";
  
  return stakes
    .filter(stake => stake.quantity > 0)
    .map(stake => `${stake.quantity} ${stake.type}`)
    .join(", ") || "No Stakes";
};

/**
 * Debounce function for API calls
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Safe async operation with error handling
 */
export const safeAsync = async <T>(
  operation: () => Promise<T>,
  fallback?: T
): Promise<T | undefined> => {
  try {
    return await operation();
  } catch (error) {
    console.error('Safe async operation failed:', error);
    return fallback;
  }
};

/**
 * Checks if user has required role
 */
export const hasUserRole = (
  userId: string | undefined,
  organiserId: string | undefined,
  role: 'organiser' | 'challenger'
): boolean => {
  if (!userId || !organiserId) return false;
  
  const isOrganiser = userId === organiserId;
  return role === 'organiser' ? isOrganiser : !isOrganiser;
};

/**
 * Gets user's streaming platforms
 */
export const getUserStreamingPlatforms = (
  streamingPlatforms: { organiser: string[]; challenger: string[] } | undefined,
  userRole: 'organiser' | 'challenger'
): string[] => {
  return streamingPlatforms?.[userRole] || [];
};
